import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import {
  SPACELIFT_VCS_REPOS_LIMIT,
  SPACELIFT_VCS_REPOS_LIMIT_STORAGE_KEY,
} from "views/Account/SpaceliftVcsRepos/Table/constants";

import useSpaceliftVcsReposFiltersVariables from "../useSearchSpaceliftVcsReposSuggestions/useFiltersVariables";
import { SEARCH_SPACELIFT_VCS_REPOS } from "./gql";
import useSpaceliftVcsReposStore from "./store";

const useSpaceliftVcsReposList = () => {
  const {
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  } = useSpaceliftVcsReposStore();
  const [storedRowsPerPage] = useLocalStorage<number>(SPACELIFT_VCS_REPOS_LIMIT_STORAGE_KEY);
  const { fullTextSearch, predicates, filterVariablesLoaded, sortOptionFields } =
    useSpaceliftVcsReposFiltersVariables();

  const first = storedRowsPerPage || SPACELIFT_VCS_REPOS_LIMIT;

  const paginationResetKey = useMemo(
    () => ({ fullTextSearch, predicates, sortOptionFields }),
    [fullTextSearch, predicates, sortOptionFields]
  );

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { data, error, networkStatus, previousData } = usePolledQuery(SEARCH_SPACELIFT_VCS_REPOS, {
    variables: {
      input: {
        fullTextSearch,
        first,
        after: cursor,
        orderBy: sortOptionFields,
        predicates,
        requestedPage,
      },
    },
    skip: !filterVariablesLoaded,
    pollingGroup: PollingIntervalGroups.Lists,
    notifyOnNetworkStatusChange: true,
  });

  const repos = useMemo(() => {
    const edges = data?.searchRepos.edges ?? previousData?.searchRepos.edges ?? [];

    return edges.map((edge) => edge.node);
  }, [data, previousData]);

  const initialLoading =
    (repos.length === 0 && networkStatus === NetworkStatus.loading) || !filterVariablesLoaded;

  const loadingContent =
    !filterVariablesLoaded ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.setVariables;

  const hasLoadedPage = Boolean(data);
  const isEmpty =
    hasLoadedPage && !repos.length && !initialLoading && !fullTextSearch && !predicates.length;
  const searchIsEmpty =
    hasLoadedPage && !repos.length && !!(fullTextSearch || predicates.length > 0);

  return {
    repos,
    error,
    initialLoading,
    loadingContent,
    isEmpty,
    searchIsEmpty,
    startCursor: data?.searchRepos?.pageInfo.startCursor,
    endCursor: data?.searchRepos?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSpaceliftVcsReposList;
